import { useDeleteLifeAreaSection } from 'features/life-areas';
import { useDeleteTasks, useTasks } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import { DeleteLifeAreaSectionDialog } from 'shared/components/dialogs/delete-life-area-section-dialog';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';

import {
  DeleteLifeAreaSectionContext,
  DeleteLifeAreaSectionContextProps,
} from './delete-life-area-section-context';

export type DeleteLifeAreaSectionProviderProps = {
  children: React.ReactNode;
};

export const DeleteLifeAreaSectionProvider: React.FC<
  DeleteLifeAreaSectionProviderProps
> = ({ children }) => {
  const [lifeArea, setLifeArea] = React.useState<LifeArea>();
  const [sectionId, setSectionId] = React.useState<ID>();
  const deleteLifeAreaSection = useDeleteLifeAreaSection();
  // Fetch tasks for the life area to filter which ones to delete
  const tasks = useTasks({ lifeAreas: [lifeArea?.id!] }, !!lifeArea?.id);
  const deleteTasks = useDeleteTasks();

  const section = useMemo(
    () => lifeArea?.sections?.find(({ id }) => id === sectionId),
    [lifeArea, sectionId],
  );

  const openDialog = useCallback((lifeArea: LifeArea, sectionId: ID) => {
    setLifeArea(lifeArea);
    setSectionId(sectionId);
  }, []);

  const closeDialog = useCallback(() => {
    setLifeArea(undefined);
    setSectionId(undefined);
  }, []);

  const submit = useCallback(
    async (withTasks: boolean) => {
      try {
        if (!lifeArea || !section) {
          return;
        }

        const tasksToDelete = withTasks
          ? tasks.filter(({ id }) => section.tasks?.includes(id))
          : [];
        if (tasksToDelete.length > 0) {
          await deleteTasks(tasksToDelete);
        }

        deleteLifeAreaSection(lifeArea, section.id, tasksToDelete);
      } finally {
        closeDialog();
      }
    },
    [closeDialog, deleteLifeAreaSection, deleteTasks, lifeArea, section, tasks],
  );

  const value = useMemo<DeleteLifeAreaSectionContextProps>(
    () => ({ onDeleteLifeAreaSection: openDialog }),
    [openDialog],
  );

  return (
    <DeleteLifeAreaSectionContext.Provider value={value}>
      {children}
      <DeleteLifeAreaSectionDialog
        open={!!section}
        name={section?.name}
        onClose={closeDialog}
        onSubmit={submit}
      />
    </DeleteLifeAreaSectionContext.Provider>
  );
};
